import {
  MIRROR_DETACH_REASON_LABELS,
  TEAM_ERROR_LABELS,
  type MirrorDetachReason,
  type StatusKind,
  type TicketEventType,
} from '@erp/shared';
import type { BadgeProps } from '@/components/ui/misc';
import { dispatchSheetErrorText } from './ticket-sheet-view';

/** Initials for the responsible avatar circle (first + last name). */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.charAt(0) ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

export function statusKindVariant(kind: StatusKind): BadgeProps['variant'] {
  switch (kind) {
    case 'open':
      return 'primary';
    case 'closed':
      return 'success';
    case 'canceled':
      return 'destructive';
    case 'custom':
      return 'default';
  }
}

/** Heuristic color for desk-scoped priority names. */
export function priorityVariant(name: string): BadgeProps['variant'] {
  const n = name.toLowerCase();
  if (n.includes('urg') || n.includes('alta') || n.includes('crít') || n.includes('crit'))
    return 'destructive';
  if (n.includes('méd') || n.includes('med') || n.includes('normal')) return 'warning';
  return 'default';
}

export const EVENT_LABELS: Record<TicketEventType, string> = {
  created: 'Ticket criado',
  updated: 'Ticket atualizado',
  stage_changed: 'Estágio alterado',
  status_changed: 'Status alterado',
  priority_changed: 'Prioridade alterada',
  responsible_changed: 'Responsável alterado',
  reparented: 'Cliente trocado',
  desk_moved: 'Movido de mesa',
  closed: 'Ticket fechado',
  canceled: 'Ticket cancelado',
  reopened: 'Ticket reaberto',
  comment_added: 'Comentário adicionado',
  appointment_added: 'Apontamento adicionado',
  appointment_deleted: 'Apontamento removido',
  // pedido da PoolTrack (02/10) — o detalhe diz o que mudou, antes → depois
  appointment_edited: 'Apontamento editado',
  timer_started: 'Timer iniciado',
  timer_stopped: 'Timer parado',
  sla_paused: 'SLA pausado',
  sla_resumed: 'SLA retomado',
  file_added: 'Anexo adicionado',
  file_deleted: 'Anexo removido',
  service_order_created: 'Ordem de serviço gerada',
  dispatch_sent: 'Disparo realizado',
  dispatch_failed: 'Disparo falhou',
  dispatch_replied: 'Cliente respondeu ao disparo',
  // spec 022 — a nota em si aparece como comentário (autor "Bot")
  bot_note_added: 'Nota do bot',
  // spec 026 — o resumo do "Puxar do TiFlux" (as mudanças têm os eventos delas)
  tiflux_pulled: 'Puxado do TiFlux',
  // spec 043 — o espelho encerrou aqui o que já tinha acabado lá (o detalhe diz como)
  tiflux_closed: 'Encerrado no TiFlux',
  // pedido da PoolTrack (02/10) — técnico responsável em campo
  technician_changed: 'Técnico responsável alterado',
};

/**
 * Rótulo do tipo com rede para um tipo que esta versão da tela não conhece
 * (a API sobe antes do SPA): texto cru em vez de "undefined".
 */
export function eventLabel(type: TicketEventType): string {
  return EVENT_LABELS[type] ?? type;
}

/** Quem fez — mora em event-detail.ts (puro, testado na API). */
export { eventActorName } from './event-detail';

/**
 * O detalhe de um evento numa linha (pedido da PoolTrack, 03/10: nenhuma chave
 * crua do payload na tela). A lógica mora em event-detail.ts, pura, para ser
 * testada na API; o Histórico usa as linhas (eventDetailLines), uma por campo.
 */
export { eventDetail } from './event-detail';

export function durationMinutes(startedAt: string | Date, endedAt: string | Date): number {
  const s = new Date(startedAt).getTime();
  const e = new Date(endedAt).getTime();
  if (Number.isNaN(s) || Number.isNaN(e)) return 0;
  return Math.max(0, Math.round((e - s) / 60_000));
}

/**
 * Rótulos pt-BR de códigos de erro TRANSVERSAIS da API — fonte única: as
 * features (chats, whatsapp, mesas…) caem aqui como fallback, então um código
 * que vale para o app todo mora neste mapa, nunca copiado por feature.
 */
const SHARED_ERROR_LABELS: Record<string, string> = {
  // pessoa fora da equipe deste tenant (pedido da PoolTrack, 05/10): ticket,
  // atividade agendada, automação, conversa, cofre, grupos de permissão
  ...TEAM_ERROR_LABELS,
  validation_error: 'Dados inválidos — revise os campos e tente de novo.',
  forbidden: 'Você não tem permissão para executar esta ação.',
  permission_denied: 'Você não tem permissão para executar esta ação.',
  // spec 011: a tela esconde os controles, mas outra aba aberta antes da
  // virada da mesa ainda pode tentar — o 409 tem de dizer o que fazer
  ticket_mirrored: 'Ticket espelhado do TiFlux — edite no TiFlux.',
  desk_mirrored:
    'Mesa espelhada do TiFlux — só recebe ticket de cliente operado no ERP. Abra ou mova o ticket no TiFlux.',
  // spec 024: desligar do espelho um ticket que nunca esteve lá
  ticket_not_mirrored: 'Este ticket não está no espelho do TiFlux — ele já é operado no ERP.',
  // spec 068 (D4): a comunicação sai com texto, com anexo, ou com os dois
  comment_empty: 'Escreva a comunicação ou anexe um arquivo.',
  comment_email_needs_text: 'Para enviar por e-mail, escreva o texto: o e-mail não leva o anexo.',
  // spec 014: disparo por ticket — a regra vive na mesa, as ações no ticket
  dispatch_cap_reached: 'A cota de disparos de hoje acabou — o ticket segue na fila para o próximo dia.',
  dispatch_not_eligible: 'O ticket não está no estágio de entrada do disparo (ou está fechado).',
  dispatch_already_sent: 'Este ticket já foi disparado.',
  dispatch_rule_disabled: 'O disparo desta mesa está desligado.',
  // spec 025 (D6): numa mesa espelhada, o disparo do ERP só alcança cliente operado no ERP
  dispatch_client_not_operated:
    'O cliente deste ticket não é operado no ERP — nesta mesa, o disparo só alcança os clientes operados.',
  dispatch_rule_not_found: 'Esta mesa não tem regra de disparo.',
  dispatch_channel_inactive: 'O canal de WhatsApp do disparo está inativo.',
  dispatch_not_queued: 'O ticket não está mais na fila de disparo.',
  stage_not_in_desk: 'Escolha estágios desta mesa.',
  technician_not_found: 'Técnico não encontrado — ele pode ter sido removido do cadastro.',
  // apontamentos (pedido da PoolTrack, 02/10: editar)
  invalid_period: 'O fim deve ser depois do início.',
  appointment_not_found: 'Apontamento não encontrado — ele pode ter sido excluído.',
  appointment_running: 'Este apontamento ainda está rodando — só dá para editar depois que o timer parar.',
  appointment_from_tiflux:
    'Este apontamento veio do TiFlux — corrija no TiFlux: a sincronização regravaria a edição feita aqui.',
  params_mismatch: 'O modelo tem outro número de parâmetros — ajuste a lista de parâmetros.',
  template_not_found: 'Modelo não encontrado na lista do canal.',
  unknown_channel: 'Canal de WhatsApp não encontrado.',
  // spec 025, fase 3: o disparo pelo número da Blip
  dispatch_channel_not_blip:
    'Nesta mesa espelhada, o disparo só sai pelo número da Blip — troque o canal da regra.',
  blip_dispatch_stopped:
    'O disparo pela Blip está parado pelo interruptor (Configurações › Integrações › WhatsApp).',
  // spec 032: o veículo e a ficha que vêm do cadastro de origem
  resource_from_sgr: 'Este recurso vem do cadastro de origem: nome, cliente e tipo não mudam aqui.',
  sheet_not_from_sgr: 'A ficha deste ticket não foi escolhida do cadastro de origem — não há o que atualizar.',
  sgr_unavailable: 'A integração com o cadastro de origem não está disponível nesta empresa.',
  // spec 035: a rotina noturna do Recurso
  sgr_routine_running: 'A rotina já está rodando (nesta réplica ou em outra) — acompanhe a rodada aqui.',
  sgr_routine_disabled: 'A rotina está desligada neste ambiente.',
  equipment_not_of_client: 'Este recurso é de outro cliente — escolha um recurso do cliente do ticket.',
  // spec 054: o motivo de fechamento da mesa
  close_reason_required: 'Escolha o motivo do fechamento: esta mesa exige um.',
  close_reason_invalid: 'Este motivo não vale mais para a mesa do ticket. Recarregue e escolha outro.',
  close_reason_exists: 'Já existe um motivo com esse nome nesta mesa.',
  close_reason_in_use: 'Este motivo já está em tickets fechados. Desative-o em vez de excluir.',
  close_reason_not_found: 'Motivo não encontrado. Recarregue a lista e tente de novo.',
  blip_reply_stage_must_equal_sent:
    'Com o número da Blip, o estágio de resposta tem de ser o de envio: é nele que o bot acha o ticket.',
};

/** Extracts a displayable message from an ApiError-shaped unknown. */
export function errorMessage(e: unknown): string {
  if (e && typeof e === 'object' && 'error' in e) {
    const msg = (e as { error: unknown }).error;
    // spec 031 (D5): a trava do ticket aberto à mão diz exatamente o que
    // preencher, com a mesa e o vocabulário do tenant — a API manda pronta
    const sheetText = dispatchSheetErrorText(e);
    if (sheetText) return sheetText;
    if (typeof msg === 'string' && msg.length > 0) return SHARED_ERROR_LABELS[msg] ?? msg;
  }
  return 'Erro inesperado. Tente novamente.';
}

/**
 * Ticket espelhado do TiFlux (spec 011): o espelho reescreve o ticket a cada
 * sync e a API recusa qualquer escrita com 409 ticket_mirrored — a tela mostra
 * o selo e esconde os controles em vez de deixar o clique falhar.
 *
 * Spec 024: quem diz é a API (`mirrored`, o predicado único do servidor), não
 * a mesa — um ticket FORA do espelho vive numa mesa espelhada e é editável.
 */
export function isTicketMirrored(ticket: { mirrored: boolean }): boolean {
  return ticket.mirrored;
}

/**
 * Fora do espelho numa mesa espelhada (spec 024): o selo "Operado no ERP" e o
 * porquê — o motivo e desde quando. null quando não há o que mostrar (mesa do
 * ERP, ou ticket espelhado).
 */
export function mirrorDetachNote(ticket: {
  desk: { origin: string };
  mirrorDetachedAt: string | Date | null;
  mirrorDetachedReason: MirrorDetachReason | null;
}): string | null {
  if (ticket.desk.origin !== 'tiflux' || ticket.mirrorDetachedAt === null) return null;
  const since = new Date(ticket.mirrorDetachedAt).toLocaleDateString('pt-BR');
  const why = ticket.mirrorDetachedReason ? MIRROR_DETACH_REASON_LABELS[ticket.mirrorDetachedReason] : null;
  return `Fora do espelho do TiFlux desde ${since}${why ? ` — ${why}` : ''}. Editado aqui; a cópia no TiFlux não é atualizada.`;
}

/**
 * Somente leitura na tela: fechado/cancelado OU espelhado. Quem ainda precisa
 * distinguir os dois (reabrir existe para o fechado, não para o espelhado)
 * olha isTicketMirrored.
 */
export function isTicketReadOnly(ticket: { status: { kind: StatusKind }; mirrored: boolean }): boolean {
  return (
    ticket.status.kind === 'closed' || ticket.status.kind === 'canceled' || isTicketMirrored(ticket)
  );
}
